import React, { useContext, useEffect, useRef, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { SessionContext } from "../../context/SessionContext";
import localizationStrings from "../../statics/localization";
import { getCinemaCopy } from "../../statics/cinemaCopy";
import {
  getMostPopularSearchContents,
  getRecentSearchContents,
  getSearchAutocomplete,
  getSearchVods
} from "../../api/vod/vod";
import VodGrid from "../vodGrid/VodGrid";
import CinemaIcon from "../cinema/CinemaIcon";
import "./Search.scss";

export const searchPath = (value) => {
  const keyword = value.trim().replace(/\s+/g, "-");
  return `/dashboard/search${keyword ? `/${encodeURIComponent(keyword)}` : ""}`;
};

export const decodeSearchTerm = (term) => {
  try {
    return decodeURIComponent(term);
  } catch (error) {
    return term;
  }
};

const normalizeList = (data) => {
  const payload = data?.Data ?? data?.data ?? data;
  const items = Array.isArray(payload)
    ? payload
    : payload?.Data || payload?.data || payload?.items || payload?.Items || [];
  return (Array.isArray(items) ? items : [])
    .map((item) => {
      const poster =
        item.posterImageUrl || item.PosterImageUrl || item.posterUrl;
      return {
        ...item,
        id:
          item.vodUId ||
          item.VodUId ||
          item.contentUId ||
          item.ContentUId ||
          item.id,
        title: item.title || item.Title || "",
        type: item.type || item.Type || "MOVIE",
        posters:
          item.posters ||
          (poster
            ? [
                { type: "Web Portrait", url: poster },
                { type: "Web Landscape", url: poster }
              ]
            : [])
      };
    })
    .filter((item) => item.id);
};

const Search = () => {
  const copy = getCinemaCopy();
  const { term: routeTerm = "" } = useParams();
  const term = decodeSearchTerm(routeTerm);
  const history = useHistory();
  const { setPath } = useContext(SessionContext);
  const [value, setValue] = useState(term.replace(/-/g, " "));
  const [results, setResults] = useState([]);
  const [popular, setPopular] = useState([]);
  const [recent, setRecent] = useState([]);
  const [filter, setFilter] = useState("ALL");
  const [emptyTab, setEmptyTab] = useState("popular");
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [retry, setRetry] = useState(0);
  const input = useRef(null);
  const navigationTimer = useRef(null);
  const suggestionVersion = useRef(0);
  const empty = !term.trim();

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(false);
    setResults([]);
    if (document.activeElement !== input.current)
      setValue(term.replace(/-/g, " "));
    setPath(searchPath(term));
    const load = async () => {
      try {
        if (empty) {
          // An unavailable history endpoint must not hide the popular catalogue.
          const responses = await Promise.allSettled([
            getMostPopularSearchContents(12),
            getRecentSearchContents(12)
          ]);
          if (!active) return;
          const popularItems =
            responses[0].status === "fulfilled"
              ? normalizeList(responses[0].value?.data)
              : [];
          const recentItems =
            responses[1].status === "fulfilled"
              ? normalizeList(responses[1].value?.data)
              : [];
          setPopular(popularItems);
          setRecent(recentItems);
          if (!popularItems.length && recentItems.length) setEmptyTab("recent");
          setError(
            responses.every((response) => response.status === "rejected")
          );
        } else {
          const { data } = await getSearchVods(term);
          if (!active) return;
          setResults([
            ...(Array.isArray(data?.channels)
              ? data.channels.map((item) => ({ ...item, type: "CHANNEL" }))
              : []),
            ...(Array.isArray(data?.contents) ? data.contents : [])
          ]);
        }
      } catch (failure) {
        if (active) setError(true);
      } finally {
        if (active) setLoading(false);
      }
    };
    load();
    return () => {
      active = false;
    };
  }, [term, retry]);

  useEffect(() => {
    const version = ++suggestionVersion.current;
    setSuggestions([]);
    if (value.trim().length < 2 || document.activeElement !== input.current)
      return;
    const timer = setTimeout(async () => {
      try {
        const { data } = await getSearchAutocomplete(value.trim(), 8);
        if (version !== suggestionVersion.current) return;
        const payload = data?.Data ?? data?.data ?? data;
        const items = payload?.Results || payload?.results || [];
        setSuggestions(
          items
            .map((item) => ({
              id: item.ContentUId || item.contentUId || item.id,
              title: item.ContentTitle || item.contentTitle || item.title,
              type: item.ContentType || item.contentType || item.type
            }))
            .filter((item) => item.title)
        );
      } catch (failure) {
        if (version === suggestionVersion.current) setSuggestions([]);
      }
    }, 250);
    return () => {
      clearTimeout(timer);
      suggestionVersion.current++;
    };
  }, [value]);

  useEffect(() => () => clearTimeout(navigationTimer.current), []);

  const navigate = (text) => {
    clearTimeout(navigationTimer.current);
    setShowSuggestions(false);
    setSuggestions([]);
    setValue(text);
    history.replace(searchPath(text));
  };
  const filtered = empty
    ? emptyTab === "recent"
      ? recent
      : popular
    : results.filter((item) => filter === "ALL" || item.type === filter);
  const filters = [
    ["ALL", localizationStrings.newSearchFilters.all],
    ["SERIES", localizationStrings.newSearchFilters.tvShow],
    ["MOVIE", localizationStrings.newSearchFilters.movie],
    ["CHANNEL", localizationStrings.newSearchFilters.channel]
  ];
  return (
    <main className="search-container">
      <form
        className="search-input-wrapper"
        role="search"
        onSubmit={(event) => {
          event.preventDefault();
          navigate(value);
        }}
      >
        <CinemaIcon name="search" className="search-leading-icon" />
        <input
          ref={input}
          className="search-input"
          aria-label={copy.search}
          autoComplete="off"
          autoFocus
          value={value}
          placeholder={localizationStrings.newSearchSuggestText}
          onChange={(event) => {
            const text = event.target.value;
            setValue(text);
            setShowSuggestions(true);
            clearTimeout(navigationTimer.current);
            navigationTimer.current = setTimeout(
              () => history.replace(searchPath(text)),
              500
            );
          }}
          onKeyDown={(event) => {
            if (event.key === "Escape") setShowSuggestions(false);
          }}
          onFocus={() => setShowSuggestions(true)}
          onBlur={(event) => {
            if (
              !event.currentTarget.parentElement.contains(event.relatedTarget)
            )
              setShowSuggestions(false);
          }}
        />
        {value && (
          <button
            type="button"
            className="search-clear"
            aria-label={copy.clearSearch}
            onClick={() => {
              navigate("");
              input.current.focus();
            }}
          >
            <CinemaIcon name="close" />
          </button>
        )}
        {showSuggestions && suggestions.length > 0 && (
          <div
            className="search-autocomplete-dropdown"
            aria-label={copy.suggestions}
          >
            {suggestions.map((item, index) => (
              <button
                type="button"
                className="search-autocomplete-item"
                key={item.id || index}
                onClick={() => navigate(item.title)}
              >
                <span className="autocomplete-title">{item.title}</span>
                <span className="autocomplete-type">{item.type}</span>
              </button>
            ))}
          </div>
        )}
      </form>
      <div className="category-row">
        <div className="category-wrapper" aria-label={copy.search}>
          {(empty
            ? [
                ["popular", copy.popularSearches],
                ["recent", localizationStrings.historySearch || copy.recent]
              ]
            : filters
          ).map(([id, label]) => (
            <button
              type="button"
              key={id}
              className={`category-item ${
                (empty ? emptyTab : filter) === id ? "selected" : ""
              }`}
              aria-pressed={(empty ? emptyTab : filter) === id}
              onClick={() => (empty ? setEmptyTab(id) : setFilter(id))}
            >
              {label}
            </button>
          ))}
        </div>
      </div>
      {loading ? (
        <div className="cinema-empty-state" role="status">
          {copy.loading}
        </div>
      ) : error ? (
        <div className="cinema-empty-state" role="alert">
          <p>{copy.loadError}</p>
          <button
            type="button"
            className="cinema-button cinema-button-secondary"
            onClick={() => setRetry((count) => count + 1)}
          >
            {copy.retry}
          </button>
        </div>
      ) : filtered.length ? (
        <VodGrid items={filtered} posterPosition={7} fromSearch />
      ) : (
        <div className="cinema-empty-state" role="status">
          <p>
            {empty
              ? copy.noItems
              : localizationStrings.formatString(
                  localizationStrings.noSearchResult,
                  { term: value }
                )}
          </p>
          {!empty && <p>{localizationStrings.sugg1}</p>}
        </div>
      )}
    </main>
  );
};
export default Search;
